import styled from '@emotion/styled';
import { css } from '@emotion/react';

import theme from 'styles/theme';

export type DateInputSize = 'sm' | 'md' | 'lg';
export type DateInputShape = 'default' | 'pill';

type FieldProps = {
  $size: DateInputSize;
  $shape: DateInputShape;
  $fullWidth?: boolean;
  $color?: string;
  $borderColor?: string;
  $backgroundColor?: string;
};

type LabelProps = {
  $color?: string;
};

const sizeStyles: Record<DateInputSize, ReturnType<typeof css>> = {
  sm: css`
    height: 32px;
    padding: 0 40px 0 12px;
    font-size: 12px;
  `,
  md: css`
    height: 38px;
    padding: 0 44px 0 16px;
    font-size: 14px;
  `,
  lg: css`
    height: 44px;
    padding: 0 48px 0 18px;
    font-size: 16px;
  `,
};

const shapeStyles: Record<DateInputShape, ReturnType<typeof css>> = {
  default: css`
    border-radius: 6px;
  `,
  pill: css`
    border-radius: 999px;
  `,
};

export const Wrapper = styled.div<{ $fullWidth?: boolean }>`
  display: inline-flex;
  flex-direction: column;
  gap: 6px;
  width: ${({ $fullWidth }) => ($fullWidth ? '100%' : 'auto')};
`;

export const Label = styled.label<LabelProps>`
  font-family: 'Mangueira', sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  color: ${({ $color }) => $color || theme.colors.primaryText};
`;

export const FieldWrap = styled.div`
  position: relative;
  display: inline-flex;
  width: 100%;
`;

export const IconSlot = styled.span`
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  pointer-events: none;
  color: ${theme.colors.gray};

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const CalendarButton = styled.button`
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: ${theme.colors.gray};
  cursor: pointer;

  svg {
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: ${theme.colors.primaryText};
    background: rgba(0, 0, 0, 0.04);
  }

  &:focus-visible {
    outline: 2px solid ${theme.colors.blue};
    outline-offset: 2px;
  }
`;

export const CalendarPopover = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 30;

  .react-datepicker {
    font-family: 'Mangueira', sans-serif;
    border: 1px solid ${theme.colors.lightGray};
    border-radius: 8px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  }

  .react-datepicker__header {
    background: #fff;
    border-bottom: 1px solid ${theme.colors.lightGray};
  }

  .react-datepicker__day--selected,
  .react-datepicker__day--keyboard-selected {
    background-color: ${theme.colors.blue};
    border-radius: 999px;
  }

  .react-datepicker__day:hover {
    border-radius: 999px;
  }
`;

export const StyledInput = styled('input', {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<FieldProps>`
  width: 100%;
  font-family: 'Mangueira', sans-serif;
  font-weight: 500;
  outline: none;
  transition: border-color 0.2s ease, opacity 0.2s ease;

  color: ${({ $color }) => $color || theme.colors.primaryText};
  background-color: ${({ $backgroundColor }) => $backgroundColor || '#fff'};
  border: 1px solid ${({ $borderColor }) => $borderColor || theme.colors.black};

  ${({ $size }) => sizeStyles[$size]}
  ${({ $shape }) => shapeStyles[$shape]}

  &::placeholder {
    color: ${theme.colors.mediumGray};
  }

  &:hover:not(:disabled) {
    border-color: ${({ $borderColor }) => $borderColor || theme.colors.black};
  }

  &:focus {
    border-color: ${({ $borderColor }) => $borderColor || theme.colors.blue};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;
